import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import {
  FaSearch,
  FaPlus,
  FaEdit,
  FaTrash,
  FaHistory,
  FaEye,
  FaBoxOpen,
  FaDollarSign,
  FaExclamationTriangle,
  FaClock,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { formatAUD } from '../../../utils/formatCurrency';
import { useAdminListOverlay } from '../../../utils/useAdminPageSearch';
import ItemDescriptionForm from './ItemDescriptionForm';
import ProductDetailModal from './ProductDetailModal';
import ItemUpdateHistory from './ItemUpdateHistory';
import BulkAddProductsModal from './BulkAddProductsModal';
import '../styles/InventoryProducts.css';

const FIND_ALL_ITEMS = `
  query FindAllItems {
    findAllItem {
      id
      name
      code
      quantity
      available_quantity
      perItemCost
      totalCost
      description
      expiryDate
      category
      unit
      reorderLevel
      maxStockLevel
      supplier
      preferredSupplier
    }
  }
`;

const DELETE_ITEM = `
  mutation RemoveItem($id: String!) {
    removeItem(id: $id)
  }
`;

const CATEGORIES = ['All', 'Chemical', 'PPE', 'Consumable', 'Tool', 'Equipment'];
const PAGE_SIZE = 8;
const EXPIRING_DAYS = 30;

function inferCategory(item) {
  const explicit = String(item?.category || '').trim();
  if (explicit) {
    const hit = CATEGORIES.find(
      (c) => c !== 'All' && c.toLowerCase() === explicit.toLowerCase(),
    );
    if (hit) return hit;
    return explicit;
  }
  const hay = `${item?.name || ''} ${item?.code || ''} ${item?.description || ''}`.toLowerCase();
  if (/chem|bleach|disinfect|detergent|acid|solvent/.test(hay)) return 'Chemical';
  if (/glove|ppe|mask|goggle|vest|helmet|respirator/.test(hay)) return 'PPE';
  if (/bag|wipe|roll|liner|paper|consumable|soap/.test(hay)) return 'Consumable';
  if (/mop|broom|bucket|squeegee|tool/.test(hay)) return 'Tool';
  if (/vacuum|washer|machine|equipment|polisher/.test(hay)) return 'Equipment';
  return 'Consumable';
}

function inferUnit(item) {
  const u = String(item?.unit || '').trim();
  if (u) return u;
  const hay = `${item?.name || ''} ${item?.description || ''}`.toLowerCase();
  if (/litre|liter|l\b|concentrate/.test(hay)) return 'Litre';
  if (/box|glove/.test(hay)) return 'Box';
  if (/roll/.test(hay)) return 'Roll';
  if (/pair/.test(hay)) return 'Pair';
  return 'Each';
}

function stockStatus(onHand, reorder) {
  const qty = Number(onHand) || 0;
  const level = Number(reorder);
  const reorderLevel = Number.isFinite(level) && level > 0 ? level : 5;
  if (qty <= 0) return { key: 'out', label: 'Out of Stock' };
  if (qty <= Math.max(1, Math.round(reorderLevel * 0.4))) {
    return { key: 'critical', label: 'Critical' };
  }
  if (qty <= reorderLevel) return { key: 'low_stock', label: 'Low Stock' };
  return { key: 'in_stock', label: 'In Stock' };
}

function formatExpiry(raw) {
  if (!raw) return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function isExpiringSoon(raw) {
  if (!raw) return false;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  const soon = new Date();
  soon.setDate(soon.getDate() + EXPIRING_DAYS);
  return d >= now && d <= soon;
}

function catClass(cat) {
  const key = String(cat || 'other')
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  return `ip-cat ip-cat--${key}`;
}

export default function InventoryProductsDashboard() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      let data;
      try {
        data = await graphqlRequest(FIND_ALL_ITEMS);
      } catch (err) {
        const msg = String(err?.message || '').toLowerCase();
        if (
          msg.includes('category') ||
          msg.includes('unit') ||
          msg.includes('reorder') ||
          msg.includes('maxstock') ||
          msg.includes('supplier')
        ) {
          data = await graphqlRequest(`
            query FindAllItemsLegacy {
              findAllItem {
                id name code quantity available_quantity
                perItemCost totalCost description expiryDate
              }
            }
          `);
        } else {
          throw err;
        }
      }
      setItems(Array.isArray(data?.findAllItem) ? data.findAllItem : []);
    } catch (e) {
      toast.error(e?.message || 'Failed to load products');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const enriched = useMemo(
    () =>
      (items || []).map((item) => {
        const cat = inferCategory(item);
        const unit = inferUnit(item);
        const onHand = Number(item.available_quantity ?? item.quantity ?? 0);
        const reorder = Number(item.reorderLevel ?? 5);
        const status = stockStatus(onHand, reorder);
        const cost = Number(item.perItemCost) || 0;
        return {
          ...item,
          category: cat,
          unit,
          onHand,
          reorderLevel: Number.isFinite(reorder) ? reorder : 5,
          status,
          cost,
          value: onHand * cost,
        };
      }),
    [items],
  );

  const overlay = useAdminListOverlay(enriched, { allowedTabs: CATEGORIES });
  const category = overlay.tab;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter((item) => {
      if (category !== 'All' && item.category !== category) return false;
      if (!q) return true;
      return `${item.name} ${item.code} ${item.category} ${item.description}`
        .toLowerCase()
        .includes(q);
    });
  }, [enriched, search, category]);

  useEffect(() => {
    setPage(1);
  }, [search, category]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = filtered.slice((pageSafe - 1) * PAGE_SIZE, pageSafe * PAGE_SIZE);

  const stats = useMemo(() => {
    const totalValue = enriched.reduce((sum, i) => sum + (i.value || 0), 0);
    return {
      total: enriched.length,
      value: totalValue,
      low: enriched.filter(
        (i) => i.status.key === 'low_stock' || i.status.key === 'critical',
      ).length,
      expiring: enriched.filter((i) => isExpiringSoon(i.expiryDate)).length,
    };
  }, [enriched]);

  const handleDelete = async (item) => {
    if (!await appConfirmMessage(`Delete product "${item.name}"?`)) return;
    try {
      await graphqlRequest(DELETE_ITEM, { id: item.id });
      toast.success('Product deleted');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to delete');
    }
  };

  return (
    <div className="ip-page">
      <header className="ip-header">
        <div>
          <h1>Inventory — Products</h1>
          <p>Office stock from Item Description. Add, edit, and track levels.</p>
        </div>
        <div className="ip-header-actions">
          <button
            type="button"
            className="ip-add ip-add--ghost"
            onClick={() => overlay.patch({ action: 'bulk' }, ['view', 'edit', 'history'])}
          >
            <FaPlus /> Bulk Add Products
          </button>
          <button
            type="button"
            className="ip-add"
            onClick={() => overlay.openAdd()}
          >
            <FaPlus /> Add Product
          </button>
        </div>
      </header>

      <section className="ip-stats">
        <article className="ip-stat">
          <div className="ip-stat-icon ip-stat-icon--purple">
            <FaBoxOpen />
          </div>
          <div>
            <div className="ip-stat-value">{stats.total}</div>
            <div className="ip-stat-label">Total Products</div>
          </div>
        </article>
        <article className="ip-stat">
          <div className="ip-stat-icon ip-stat-icon--green">
            <FaDollarSign />
          </div>
          <div>
            <div className="ip-stat-value">{formatAUD(stats.value)}</div>
            <div className="ip-stat-label">Total Value</div>
          </div>
        </article>
        <article className="ip-stat">
          <div className="ip-stat-icon ip-stat-icon--orange">
            <FaExclamationTriangle />
          </div>
          <div>
            <div className="ip-stat-value">{stats.low}</div>
            <div className="ip-stat-label">Low Stock</div>
          </div>
        </article>
        <article className="ip-stat">
          <div className="ip-stat-icon ip-stat-icon--red">
            <FaClock />
          </div>
          <div>
            <div className="ip-stat-value">{stats.expiring}</div>
            <div className="ip-stat-label">Expiring Soon</div>
          </div>
        </article>
      </section>

      <div className="ip-panel">
        <div className="ip-filters">
          <label className="ip-search">
            <FaSearch />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, code, or category..."
            />
          </label>
          <div className="ip-pills">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                className={`ip-pill ${category === c ? 'is-active' : ''}`}
                onClick={() => overlay.setTab(c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="ip-table-wrap">
        <table className="ip-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Product Name</th>
              <th>Category</th>
              <th>Unit</th>
              <th>Cost/Unit</th>
              <th>Current Stock</th>
              <th>Reorder Level</th>
              <th>Stock Status</th>
              <th>Expiry</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10" className="ip-empty">
                  Loading products…
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan="10" className="ip-empty">
                  No products found. Click Add Product to create one.
                </td>
              </tr>
            ) : (
              pageRows.map((item) => (
                <tr key={item.id}>
                  <td className="ip-sku">{item.code || '—'}</td>
                  <td>
                    <div className="ip-name">{item.name}</div>
                    {item.description ? (
                      <div className="ip-desc">{item.description}</div>
                    ) : null}
                  </td>
                  <td>
                    <span className={catClass(item.category)}>{item.category}</span>
                  </td>
                  <td>{item.unit}</td>
                  <td>{item.cost ? formatAUD(item.cost) : '—'}</td>
                  <td>
                    {item.onHand} {item.unit}
                  </td>
                  <td>{item.reorderLevel}</td>
                  <td>
                    <span className={`ip-status ip-status--${item.status.key}`}>
                      {item.status.label}
                    </span>
                  </td>
                  <td>{formatExpiry(item.expiryDate)}</td>
                  <td>
                    <div className="ip-actions">
                      <button
                        type="button"
                        className="ip-icon-btn"
                        title="View"
                        onClick={() => overlay.openView(item)}
                      >
                        <FaEye />
                      </button>
                      <button
                        type="button"
                        className="ip-icon-btn"
                        title="Edit"
                        onClick={() => overlay.openEdit(item)}
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="ip-icon-btn"
                        title="History"
                        onClick={() => overlay.openHistory(item)}
                      >
                        <FaHistory />
                      </button>
                      <button
                        type="button"
                        className="ip-icon-btn danger"
                        title="Delete"
                        onClick={() => void handleDelete(item)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="ip-foot">
          <span>
            Showing {pageRows.length} of {filtered.length} products
          </span>
          <div className="ip-pages">
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .slice(0, 8)
              .map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`ip-page-btn ${pageSafe === n ? 'is-active' : ''}`}
                  onClick={() => setPage(n)}
                >
                  {n}
                </button>
              ))}
          </div>
        </div>
        </div>
      </div>

      {overlay.showForm ? (
        <ItemDescriptionForm
          onClose={overlay.closeOverlay}
          onItemAdded={load}
          editData={overlay.editing}
        />
      ) : null}

      {overlay.action === 'bulk' ? (
        <BulkAddProductsModal
          onClose={overlay.closeOverlay}
          onSaved={load}
        />
      ) : null}

      {overlay.historyItem ? (
        <ItemUpdateHistory
          item={overlay.historyItem}
          onClose={overlay.closeOverlay}
        />
      ) : null}

      {overlay.viewing ? (
        <ProductDetailModal
          item={overlay.viewing}
          onClose={overlay.closeOverlay}
          onEdit={overlay.openEdit}
        />
      ) : null}
    </div>
  );
}
